import Skeleton from '@material-ui/lab/Skeleton';
import clsx from 'clsx';
import { ENTITIES } from 'features/inventory-profiles/constants';
import { formatDateTime } from 'features/inventory-profiles/utils';
import usePageTitle from 'hooks/usePageTitle';
import strings from 'strings';
import FullWidthCardHeader from 'UI/components/templates/FullWidthCardHeader';

import { LayoutBase } from '../LayoutBase';
import { ProfileContentSkeleton } from '../ProfileContentSkeleton';
import { ProfileEmptyState } from '../ProfileEmptyState';

import { useStyles } from './ProfilePageLayout.styled';

const COPIES = strings.inventoryProfiles.common;
const SKELETON_WIDTH = 150;

/**
 * Places the slots of a profile on top of FullWidthCardHeader (V1) and owns loading,
 * error states and the page title. It receives no entity object: `header.entityType`
 * is the key of ENTITIES and it is all the layout needs to label the section.
 * @param {Object} props
 * @param {{ entityType: string, title?: string, lastActivity?: string }} props.header
 * @param {React.ReactNode} [props.actions]
 * @param {boolean} [props.hasHeaderActions] `actions` has buttons besides the ⋮ menu
 * @param {React.ReactNode} [props.subheader]
 * @param {boolean} [props.isLoading]
 * @param {{ statusCode: number } | null} [props.error]
 * @param {() => void} [props.onRetry] "Try again" of the server error state; reloads the page by default
 * @param {boolean} [props.enableUrlNavigation] Back navigation and route styling; false = quick view.
 *   It doesn't reach `children`: pass the same flag to ProfileTabs to sync the active tab with the URL
 * @param {string} [props.layoutClassName] Extra className on the content page area (for quick view consumers)
 * @param {React.ReactNode} props.children
 */
export const ProfilePageLayout = ({
  actions = null,
  children,
  enableUrlNavigation = true,
  error = null,
  hasHeaderActions = false,
  header,
  isLoading = false,
  layoutClassName,
  onRetry,
  subheader = null
}) => {
  usePageTitle({ title: header.title, isLoading, shouldLoadTitle: enableUrlNavigation });
  const classes = useStyles();

  if (error) {
    return (
      <ProfileEmptyState
        entityType={header.entityType}
        statusCode={error.statusCode}
        onRetry={onRetry}
      />
    );
  }

  const skeleton = <Skeleton variant="text" width={SKELETON_WIDTH} />;
  const lastActivityDate = formatDateTime(header.lastActivity);
  const subtitle = lastActivityDate ? (
    <>
      <strong>{COPIES.lastActivity}</strong> {lastActivityDate}
    </>
  ) : (
    COPIES.noRecentActivity
  );

  return (
    <FullWidthCardHeader
      additionalCardClasses={!enableUrlNavigation ? classes.containedProfile : undefined}
      contentPageLayoutProps={{
        className: clsx(layoutClassName, isLoading && classes.loadingLayout)
      }}
      isLoading={isLoading}
      headerCardProps={{
        sectionName: ENTITIES[header.entityType]?.sectionName,
        title: header.title,
        titleLabelProps: { backNavigation: enableUrlNavigation }
      }}
      subtitle={subtitle}
      actionsBar={actions}
      additionalHeaderActions={hasHeaderActions}
      headerContent={
        isLoading ? <LayoutBase locationInfo={skeleton} summaryInfo={skeleton} /> : subheader
      }
      pageContent={isLoading ? <ProfileContentSkeleton /> : children}
    />
  );
};
